import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { name: "current_password", label: "Current Password", testid: "change-password-current-input" },
  { name: "new_password", label: "New Password (min. 12 characters)", testid: "change-password-new-input" },
  { name: "confirm", label: "Confirm New Password", testid: "change-password-confirm-input" },
];

export default function ChangePassword() {
  const { logout } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 12) { setError("New password must be at least 12 characters"); return; }
    if (form.new_password !== form.confirm) { setError("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password changed. Please log in with your new password.");
      logout();
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired, please log in again"); logout(); return; }
      setError(formatApiError(err.response?.data?.detail) || "Could not change password");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-change-password-page">
      <div className="mb-6 flex items-center gap-3">
        <KeyRound className="h-6 w-6 text-[#C59B27]" />
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">Account</p>
          <h1 className="font-display text-3xl font-semibold text-[#194324]">Change Password</h1>
        </div>
      </div>
      <form onSubmit={submit} className="bg-white rounded-xl border border-[#E2DAD0] p-6 max-w-md space-y-4" autoComplete="off">
        {error && <div className="bg-[#FFF0E8] border border-[#D9531E]/30 text-[#D9531E] text-sm rounded-lg px-4 py-2.5" data-testid="change-password-error">{error}</div>}
        {FIELDS.map((f) => (
          <div key={f.name} className="space-y-2">
            <Label htmlFor={f.name}>{f.label}</Label>
            <Input id={f.name} type={show ? "text" : "password"} autoComplete={f.name === "current_password" ? "current-password" : "new-password"} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} required data-testid={f.testid} />
          </div>
        ))}
        <label className="flex items-center gap-2 text-sm text-[#3A4E41]">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} data-testid="change-password-show-toggle" /> Show passwords
        </label>
        <Button type="submit" disabled={saving} className="w-full bg-[#194324] hover:bg-[#2D623A] text-white font-semibold h-11" data-testid="change-password-submit-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change Password"}
        </Button>
      </form>
    </div>
  );
}
